import { HeadingTag } from '@/globals/HeadingTag';
import { Component, Host, Prop, h, State, Fragment, Watch } from '@stencil/core';
import { ScreenWidth, ScreenWidthInfo, DEFAULT_BREAKPOINTS } from '@/globals/ScreensizeDetect';
import { HeadingLevel } from '@/globals/types';
import { angle_down, angle_up } from 'pn-design-assets/pn-assets/icons.js';
import { JSX } from 'react';
import { PnChatService } from '../pn-chat/pn-chat.service';
import { HeaderConfig, TableRowData, TableRowi18n } from './types'
import { translations } from './translations';
import { MarketwebTableService } from './pn-marketweb-table.service';

@Component({
  tag: 'pn-marketweb-table',
  styleUrl: 'pn-marketweb-table.scss',
})
export class PnMarketWebTable {
  @Prop() mainHeading?: string;
  @Prop() mainHeadingLevel: HeadingLevel = 'h2';
  @Prop() preamble: string
  @Prop() subHeadingLevel: HeadingLevel = 'h3';
  @Prop() headers!: string | HeaderConfig[];
  @Prop() rows!: string | TableRowData[];
  @Prop() showHeadersInCells: boolean = false;
  @Prop() cellHeaderLevel: HeadingLevel = 'h3';
  @Prop() tableRowCtaActionLabel: string;
  @Prop() imageColAriaHeader: string;
  @Prop() ctaColAriaHeader: string;
  @Prop() checkForNewShipments: boolean = false;
  @Prop() endpoint: string;
  @Prop() language: string = 'sv';
  @Prop() market: string;
  @Prop() toggleDetailLabel: string = 'View details';
  @Prop() checkForChat: boolean = false;
  @Prop({ reflect: true, mutable: true }) value: string = '';
  @Prop() shipmentType!: string;
  @Prop() contentId: string = '';
  @Prop() takeAmountRow: number = 2;
  @Prop() shipmentCount: number = 2;

  @State() i18n: TableRowi18n;
  @State() private _headers: HeaderConfig[] = [];
  @State() private _rows: TableRowData[] = [];
  @State() private _allKeys: Set<string> = new Set();
  @State() private screenInfo?: ScreenWidthInfo;

  private screenWidth = ScreenWidth.getInstance();
  private cleanup?: () => void;
  private _chatService: PnChatService;
  private _chatSegment: string = "#chat";
  private _marketwebTableService: MarketwebTableService;

  componentWillLoad(): void {
    this.setInitialValues();
    this.setTranslations();
    this._marketwebTableService = new MarketwebTableService(this.endpoint, this.language);

    this.cleanup = this.screenWidth.subscribe(info => {
      this.screenInfo = info;
    });

    if (this.checkForNewShipments) {
      this._chatService = new PnChatService(this.endpoint, this.market, this.language);
      setInterval(() => {
        this.checkForNewMessages();
      }, 60000);
    }
  }

  setTranslations() {
    if (translations[this.language]) {
      this.i18n = translations[this.language] as TableRowi18n;
    }
    else {
      const fallbackLang = 'sv';
      this.i18n = translations[fallbackLang] as TableRowi18n;
    }
  }

  @Watch('language')
  onLanguageChange() {
    this.setTranslations();
  }

  async componentDidLoad() {
    if (this.checkForChat) {
      const thisTable = document.querySelector('pn-marketweb-table.' + this.shipmentType);
      const chatHrefs = thisTable.querySelectorAll('[href*="#chat"]');
      const openChatATags: HTMLAnchorElement[] = Array.from(chatHrefs).filter(
        (el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement
      );
      this.OpenChatEvent(openChatATags);
    }
  }

  componentDidRender() {
    const thisTable = document.querySelector('pn-marketweb-table[shipment-type=' + this.shipmentType + ']');
    const toggleButtons = thisTable?.querySelectorAll('pn-button.toggle-widget');
    toggleButtons?.forEach(pnButton => {
      const btnText = pnButton.querySelector('.pn-button-text');
      if (btnText && btnText.innerHTML !== this.i18n.toggleButtonOn) {
        btnText.innerHTML = this.i18n.toggleButtonOn;
      }
      const button = pnButton.querySelector('button');
      if (button && !button.hasAttribute('data-toggle-initialized')) {
        button.addEventListener('click', this.toggleButton.bind(this));
        button.setAttribute('data-toggle-initialized', 'true');
      }
    });
  }

  private toggleButton(event) {
    const button = event.currentTarget as HTMLButtonElement;
    const parentRow = button.closest('tr');
    const parentPnButton = button.closest('pn-button');

    const isActive = parentRow.classList.contains("shipment-id-active");

    const existingWidgetRows = document.querySelectorAll('.widget-row');
    existingWidgetRows.forEach(row => row.remove());

    const allRows = document.querySelectorAll('tr');
    allRows.forEach(row => row.classList.remove("shipment-id-active"));

    const allPnButtons = document.querySelectorAll('pn-button.toggle-widget');
    allPnButtons.forEach(pnBtn => {
      pnBtn.setAttribute('icon', angle_down);
      pnBtn.querySelector('.pn-button-text').innerHTML = this.i18n.toggleButtonOn;
    });

    if (isActive) {
      return;
    }
    button.querySelector('.pn-button-text').innerHTML = this.i18n.toggleButtonOff;
    parentRow.classList.add("shipment-id-active");

    const widgetRow = document.createElement('tr');
    widgetRow.classList.add('widget-row');

    const tdElement = document.createElement('td');
    tdElement.colSpan = parentRow.children.length;

    const divEl = document.createElement('div');
    divEl.classList.add('pn-widget-wrapper');

    const widget = document.createElement('pn-widget');
    widget.setAttribute('locale', this.language);
    widget.setAttribute('shipment-id', button.id);

    divEl.appendChild(widget);
    tdElement.appendChild(divEl);
    widgetRow.appendChild(tdElement);

    parentRow.parentNode.insertBefore(widgetRow, parentRow.nextSibling);

    parentPnButton.setAttribute('icon', angle_up);
  }

  private handleShowMore = async () => {
    let skip = this._rows.length;
    let data = await this._marketwebTableService.getUserShipments(this.contentId, skip, this.takeAmountRow, this.checkForChat);
    if (data) {
      this._rows = [...this._rows, ...data];
    }
  };

  async waitForComponent(selector: string, maxRetries = 10, delay = 300): Promise<HTMLElement | null> {
    for (let i = 0; i < maxRetries; i++) {
      const element = document.querySelector(selector);
      if (element) {
        return element as HTMLElement;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    return null;
  }

  disconnectedCallback(): void {
    if (this.cleanup) {
      this.cleanup();
    }
  }

  private async checkForNewMessages() {
    for (let i = 0; i < this._rows.length; i++) {
      const row = this._rows[i];
      const itemId = row.id;

      if (itemId) {
        const hasNewMessages = await this._chatService.hasNewMessages(itemId);

        if (row.hasNewMessages !== hasNewMessages) {
          this._rows[i] = { ...row, hasNewMessages: hasNewMessages };
        }
      }
    }

    this._rows = [...this._rows];
  }

  private setInitialValues(): void {
    try {
      this.parseRows();
      this.parseHeaders();

    } catch (error) {
      console.error('Error parsing table data:', error);
      this._rows = [];
      this._headers = [];
    }
  }

  private parseRows() {
    this._rows = typeof this.rows === 'string' ? JSON.parse(this.rows) : this.rows;
    this._allKeys = new Set(this._rows.flatMap(row => Object.keys(row)));
  }

  private parseHeaders() {
    if (typeof this.headers === 'string') {
      this._headers = JSON.parse(this.headers);
    } else if (this.headers) {
      this._headers = this.headers;
    } else {
      this._headers = Array.from(this._allKeys).map(key => ({ key, label: key }));
    }
  }

  private renderCellContent(header: HeaderConfig, row: TableRowData): JSX.Element | string | number | boolean {
    const content = header.render ? header.render(row) : row[header.key] ?? '-';
    return content;
  }

  private renderCell(header: HeaderConfig, row: TableRowData): JSX.Element {
    const content = this.renderCellContent(header, row);

    if (this.showHeadersInCells || this.screenInfo.width <= DEFAULT_BREAKPOINTS.tablet) {
      return (
        <div class="cell-content" role="cell">
          <HeadingTag
            level={this.cellHeaderLevel}
            cssClass="cell-heading"
            additionalProps={{
              'aria-hidden': 'true',
              'role': 'presentation',
            }}
          >
            {header.label}
          </HeadingTag>
          <span class="cell-value">{content}</span>
        </div>
      );
    }

    return <>{content}</>;
  }

  private handleToggleClick = () => {
    const areaToggle = document.querySelector('.trackandtracewidgetblock');
    if (areaToggle?.parentElement) {
      areaToggle.parentElement.classList.toggle('hideRow');
    }
  };

  private renderActionButtons(row: TableRowData): JSX.Element | null {
    if (!row.actionButtons) return null;

    return (
      <td class="actions" role="cell">
        <div class="button-wrapper" role="group" aria-label={this.tableRowCtaActionLabel} data-label={this.i18n.newMessage}>
          {row.actionButtons.map(btn => (
            <pn-button
              appearance={btn.appearance}
              class={btn.class}
              variant={btn.variant}
              small={btn.small}
              loading={btn.loading}
              icon={btn.icon}
              leftIcon={btn.leftIcon}
              tooltip={btn.tooltip}
              href={btn.href}
              rel={btn.rel}
              download={btn.download}
              form={btn.form}
              buttonId={btn.buttonId}
              icon-only={btn.iconOnly}
              arialabel={btn.content}
              data-chat={btn.customAttribute}>
              {btn.content}
            </pn-button>
          ))}
        </div>
      </td>
    );
  }

  private async OpenChatEvent(aTags: HTMLAnchorElement[]) {
    aTags.forEach((aTag) => {
      aTag.addEventListener("click", (e) => {
        e.preventDefault();
        const previousLocationHash = window?.location?.hash ?? "";

        const findShipmentIdInUrlRegex = /#chat:([a-zA-Z0-9]+)(?=#|$)/;
        const match = aTag.href.match(findShipmentIdInUrlRegex);
        const shipmentId = match ? match[1] : "";
        const shipmentHash = `${this._chatSegment}:${shipmentId}`;
        window.location.hash = shipmentHash;

        this.waitForComponent('pn-chat').then((chat) => {
          if (shipmentId) {
            chat.setAttribute('item-id', shipmentId);
          }
          else {
            chat.setAttribute('item-id', "");
          }

          let oldInitChatId = chat.getAttribute('pn-chat-id');

          const _defaultChatId = "00000000-0000-0000-0000-000000000000";
          const _defaultAlternativeChatId = "1";
          const _defaultTriggerChatId = oldInitChatId === _defaultChatId ? _defaultAlternativeChatId : _defaultChatId;
          let chatId = _defaultTriggerChatId;
          const reversedChatId = aTag?.parentElement?.getAttribute('data-chat');

          if (reversedChatId) {
            chatId = reversedChatId.split('').reverse().join('');
          }

          chat.setAttribute('is-logged-in', 'true');
          chat.setAttribute('pn-chat-id', chatId);

          if (previousLocationHash.includes(shipmentHash)) {
            let forceOpen = chat.getAttribute('force-open') ?? 'true';
            forceOpen = forceOpen === 'true' ? 'false' : 'true';
            chat.setAttribute('force-open', forceOpen);
          }
        });
      });
    });
  }

  render() {
    const tableId = `table-${Math.random().toString(36)}`;
    const headingId = `${tableId}-main-heading`;

    return (
      <Host language={this.language}>
        <div class='pn-marketweb-table'>
          <div role='region' aria-labelledby={this.mainHeading ? headingId : undefined}>
            <div class='heading-section'>
              <div class='heading-wrapper'>
                {this.mainHeading && (
                  <HeadingTag level={this.mainHeadingLevel} cssClass='main-heading' id={headingId}>
                    {this.mainHeading}
                  </HeadingTag>
                )}
                {this.preamble && (
                  <div class="preamble-section">{this.preamble}</div>
                )}
              </div>
            </div>
            {this._rows.length > 0 && (
              <table class='pn-marketweb-table__table' aria-labelledby={this.mainHeading ? `${tableId}-main-heading` : undefined}>
                {this._headers && (
                  <thead class={this.showHeadersInCells ? 'sr-only' : undefined} >
                    <tr >
                      {this._rows.some((row) => row.imageUrl) && this.imageColAriaHeader && (
                        <th scope='col' class='sr-only'>
                          {this.imageColAriaHeader}
                        </th>
                      )}
                      {this._headers.map((header) => (
                        <th key={header.key} scope='col' class='sr-only-mobile'>
                          {header.label}
                        </th>
                      ))}
                      {this._rows.some((row) => row.link1 || row.link2) && this.ctaColAriaHeader && (
                        <th scope='col' class='sr-only'>
                          {this.ctaColAriaHeader}
                        </th>
                      )}
                    </tr>
                  </thead>
                )}
                <tbody class="pn-marketweb-table__table-body">
                  {this._rows.slice(0, this.shipmentCount).map((row, rowIndex) => (
                    <tr key={rowIndex} class={`cell-row ${row.hasNewMessages ? "row-new-message" : ''}`}>
                      {row.imageUrl && (
                        <td class='cell-image'>
                          <div class='image-container'>
                            <img src={row.imageUrl} alt='' class='row-image' />
                          </div>
                        </td>
                      )}
                      {this._headers.map((header) => (
                        <td key={`${rowIndex}-${header.key}`} class='cell-content' >
                          {this.renderCell(header, row)}
                        </td>
                      ))}
                      {this.renderActionButtons(row)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {this.shipmentCount > this._rows.length && (
              <div class="pn-market-table__show-more-wrapper">
                <pn-button variant="outlined" appearance="light" label={this.i18n?.showMoreLabel || 'Show More'} onClick={() => this.handleShowMore()}></pn-button>
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
